package org.example.project.enginevertrieb

import androidx.compose.runtime.key
import androidx.compose.runtime.LaunchedEffect
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.service.RollenService
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.coroutines.CancellationException
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.META_HEADERS
import org.example.project.utils.All

@Composable
fun Vertriebspool() {
    var eintraege by remember { mutableStateOf<List<All>>(emptyList()) }
    var zusatzHeaders by remember { mutableStateOf<List<VertriebsindexApi.ZusatzHeader>>(emptyList()) }
    var headerDialogOpen by remember { mutableStateOf(false) }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(emptyMap()) }
    val scope = rememberCoroutineScope()
    var tabellenVersion by remember { mutableStateOf(0) }
    var markierteFirmen by remember { mutableStateOf<Set<String>>(emptySet()) }
    var rueckgabeLaeuft by remember { mutableStateOf(false) }
    var rueckgabeFehler by remember { mutableStateOf("") }
    var aktionLaeuft by remember { mutableStateOf(false) }
    var infoLeadId by remember { mutableStateOf<String?>(null) }
    val selected = eintraege.filter { it.id in markierteFirmen }
    val busy = rueckgabeLaeuft || aktionLaeuft
    val canReturnToIndex = RollenService.hasActionPermission("Vertriebspool", RollenService.ACTION_EDIT) && !busy && selected.isNotEmpty() && selected.size == markierteFirmen.size
    val canAssignLeads = RollenService.hasActionPermission("Vertriebspool", RollenService.ACTION_EDIT) && !busy && selected.isNotEmpty() && selected.size == markierteFirmen.size
    val infoLead = eintraege.firstOrNull { it.id == infoLeadId }

    suspend fun reloadVertriebsindex(vollstaendig: Boolean = false) {
        if (vollstaendig) letzteFilter = emptyMap()
        val filters = letzteFilter
        eintraege = if (filters == null) {
            emptyList()
        } else {
            VertriebsindexApi.searchEintraege(filters)
                .filter { it.status in setOf("Vorbereitet") }
                .sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
        }
        if (vollstaendig) {
            markierteFirmen = emptySet()
            tabellenVersion++
        }
    }

    fun work(action: suspend () -> Unit) {
        if (rueckgabeLaeuft || aktionLaeuft) return
        aktionLaeuft = true
        rueckgabeFehler = ""
        scope.launch {
            try { action() }
            catch (e: CancellationException) { throw e }
            catch (e: Exception) { rueckgabeFehler = e.message ?: "Aktion fehlgeschlagen. Bitte aktualisieren." }
            finally { aktionLaeuft = false }
        }
    }

    LaunchedEffect(Unit) {
        work { reloadVertriebsindex() }
        try { zusatzHeaders = VertriebsindexApi.loadHeaders() }
        catch (e: CancellationException) { throw e }
        catch (e: Exception) { rueckgabeFehler = e.message ?: "Header konnten nicht geladen werden." }
    }

    if (headerDialogOpen) HeaderHinzufuegenDialog("vertriebspool-header", zusatzHeaders, { headerDialogOpen = false }) {
        zusatzHeaders = it
        tabellenVersion++
    }
    if (infoLead != null) LeadInfoReadOnlyPopup(infoLead) { infoLeadId = null }

    if (rueckgabeFehler.isNotBlank()) {
        Div({ attr("role", "alert") }) { Text(rueckgabeFehler) }
    }
    
    
    key(tabellenVersion) {
    TabularScreen(
        screenKey = "Vertriebspool",
        rollenKey = "Vertriebspool",
        title = "Vertriebspool",
        fixedLeadingColumns = vertriebFixedLeadingHeaders().take(4),
        fixedTrailingColumns = META_HEADERS,
        headers = vertriebTabularHeaders(zusatzHeaders),
        headerDisplayLabels = vertriebTabularLabels(zusatzHeaders),
        toggleColumns = zusatzHeaders.filter { it.typ == "CHECKBOX" }.map { it.id }.toSet(),
        models = eintraege,
        onSelectedRowsChange = { markierteFirmen = it },
        data = eintraege.map { it.id to vertriebRow(it, zusatzHeaders) },
        editable = false,
        allowCreate = false,
        allowSave = false,
        allowDelete = false,
        allowImportButton = false,
        allowExportButton = false,
        allowSettingsButton = false,
        extraNavButtons = listOf(
            headerButton(RollenService.hasActionPermission("Vertriebsindex", RollenService.ACTION_EDIT)) {
                headerDialogOpen = true
            },
            TabularToolbarButton("Lead-Info", "images/info.svg", selected.size == 1, onClick = {
                infoLeadId = selected.singleOrNull()?.id
            }),
            TabularToolbarButton("Lead zuweisen", "images/userverwaltung.svg", canAssignLeads,
                onClick = {
                    val leadIds = selected.map { it.id }
                    if (leadIds.isEmpty()) return@TabularToolbarButton
                    work {
                        VertriebsindexApi.assignLeadsToSelf(leadIds)
                        eintraege = eintraege.filterNot { it.id in leadIds }
                        markierteFirmen = emptySet()
                    }
                }),
            TabularToolbarButton("Zurück zur Liste", "images/back.svg",
                canReturnToIndex,
                onClick = {
                    val ids = selected.map { it.id }
                    rueckgabeLaeuft = true
                    rueckgabeFehler = ""
                    scope.launch {
                        try {
                            VertriebsindexApi.returnFromVertriebspool(ids)
                            eintraege = eintraege.filterNot { it.id in ids }
                            markierteFirmen = emptySet()
                        } catch (e: Exception) { rueckgabeFehler = e.message.orEmpty() }
                        finally { rueckgabeLaeuft = false }
                    }
                })
        ), 
        onRefresh = {
            work {
                zusatzHeaders = VertriebsindexApi.loadHeaders()
                reloadVertriebsindex(vollstaendig = true)
            }
        },
        onSearchWithFilters = { filters ->
            work {
                letzteFilter = filters
                reloadVertriebsindex()
            }
        },
        allowSearchButton = true,
        showFilterRow = true,
        onCommit = {}
    )
    }
}
